import React, { FC, useEffect, ReactElement } from 'react'
import * as Sentry from '@sentry/react'
import { getStatuses } from '../../../../Graphql-Utilities'
import { useDateTimeDuration } from '../../../../Hooks'
import { Tag, Typography } from 'antd'
import { s } from '../style'
import { APP_SENTRY_LOGGING } from '../../../../Constants'
import { EventMenuItemEventFragment } from './EventMenuItem.generated-types'
import { ProposalStatus } from '../../../../types'

const { Text, Link } = Typography

interface EventMenuItemProps {
  event: EventMenuItemEventFragment
  isArchived: boolean
}

interface EventMenuItemBaseProps {
  name: string
  date: string
  timings?: string
}

interface StatusEventMenuItemProps extends EventMenuItemBaseProps {
  responseCount: number
}

interface GreyedOutEventMenuItemProps extends EventMenuItemBaseProps {
  title: string
}

interface FallBackEventMenuItemProps extends GreyedOutEventMenuItemProps {
  event: EventMenuItemEventFragment
}

export const EventMenuItem: FC<EventMenuItemProps> = ({
  event,
  event: { name, proposals },
  isArchived,
}): ReactElement => {
  const statuses = getStatuses(event)
  const { durationMedDate, durationRange } = useDateTimeDuration(event)

  // Proposals are not set to rejected when the invitation is declined, so filter them out manually.
  // TODO: remove these when the backend is updated.
  const invitationIdsToInvitations = new Map(event.invitations.map((invitation) => [invitation.ID, invitation]))
  const filteredProposals = proposals.filter((proposal) => !invitationIdsToInvitations.get(proposal.invitationID)?.declinedAt)

  const proposalCount = filteredProposals.length
  const proposalSentToGuestCount = filteredProposals.filter(
    (p) => p.status === ProposalStatus.SentToGuest
  ).length

  if (isArchived) {
    if (statuses.isBookingCancelled) {
      return (
        <GreyedOutEventMenuItem
          name={name}
          date={durationMedDate}
          timings={durationRange}
          title={'Cancelled event'}
        />
      )
    } else if (statuses.noResponseWasChosen) {
      return (
        <GreyedOutEventMenuItem
          name={event.name}
          date={durationMedDate}
          timings={durationRange}
          title={'No response chosen'}
        />
      )
    } else {
      return (
        <GreyedOutEventMenuItem
          name={event.name}
          date={durationMedDate}
          timings={durationRange}
          title={'Past event'}
        />
      )
    }
  }

  if (statuses.isAwaitingResponses || filteredProposals.length === 0) {
    return (
      <EventAwaitingResponsesMenuItem
        name={name}
        date={durationMedDate}
        timings={durationRange}
        responseCount={proposalCount}
      />
    )
  }

  if (statuses.isActiveWithResponses) {
    return (
      <AwaitingDecisionEventMenuItem
        name={name}
        date={durationMedDate}
        timings={durationRange}
        responseCount={proposalSentToGuestCount}
      />
    )
  }

  if (statuses.isRequestedToBook) {
    return (
      <RequestedToBookEventMenuItem
        name={name}
        date={durationMedDate}
        timings={durationRange}
        responseCount={proposalCount}
      />
    )
  }

  if (statuses.isBookingConfirmed) {
    return (
      <ConfirmedEventMenuItem
        name={name}
        date={durationMedDate}
        timings={durationRange}
        responseCount={proposalCount}
      />
    )
  }

  if (statuses.isClosed) {
    return (
      <ClosedEventMenuItem
        name={name}
        date={durationMedDate}
        timings={durationRange}
        responseCount={proposalCount}
      />
    )
  }

  return (
    <FallBackEventMenuItem
      name={name}
      date={durationMedDate}
      timings={durationRange}
      title={event.status.replace(/([A-Z])/g, ' $1')}
      event={event}
    />
  )
}

const EventMenuItemBase: FC<EventMenuItemBaseProps> = ({ children, name, date, timings }) => (
  <div style={s.menuItem.base}>
    {children}
    <Text style={s.menuItem.name}>{name}</Text>
    <Text style={s.menuItem.date}>{date}</Text>
    {timings && <Text style={s.menuItem.timings}>{timings}</Text>}
  </div>
)

const GreyedOutEventMenuItem: FC<GreyedOutEventMenuItemProps> = ({
  name,
  date,
  timings,
  title,
}) => (
  <EventMenuItemBase name={name} date={date} timings={timings}>
    <Link style={{ ...s.menuItem.title.common, color: s.menuItem.title.archiveColor }}>
      {title}
    </Link>
  </EventMenuItemBase>
)

const FallBackEventMenuItem: FC<FallBackEventMenuItemProps> = ({ event, ...props }) => {
  useEffect(() => {
    APP_SENTRY_LOGGING &&
      Sentry.captureException({ message: 'FallBackEventMenuItem component rendered', event })
  }, [])

  return <GreyedOutEventMenuItem {...props} />
}

const AwaitingDecisionEventMenuItem: FC<StatusEventMenuItemProps> = ({
  name,
  date,
  timings,
  responseCount,
}) => {
  return (
    <EventMenuItemBase name={name} date={date} timings={timings}>
      <div style={s.menuItem.header}>
        <Link style={s.menuItem.title.common}>
          {'Pick a venue'}
        </Link>
        <Tag style={s.menuItem.tag.style} color={s.menuItem.tag.color}>
          {responseCount > 1
            ? `${responseCount} proposals`
            : `${responseCount} proposal`}
        </Tag>
      </div>
    </EventMenuItemBase>
  )
}

const RequestedToBookEventMenuItem: FC<StatusEventMenuItemProps> = ({ name, date, timings }) => {
  return (
    <EventMenuItemBase name={name} date={date} timings={timings}>
      <Link style={s.menuItem.title.common}>
        {'Requested to book'}
      </Link>
    </EventMenuItemBase>
  )
}

const ClosedEventMenuItem: FC<StatusEventMenuItemProps> = ({ name, date, timings }) => {
  return (
    <EventMenuItemBase name={name} date={date} timings={timings}>
      <Link style={s.menuItem.title.common}>
        {'Booking confirmed'}
      </Link>
    </EventMenuItemBase>
  )
}

const ConfirmedEventMenuItem: FC<StatusEventMenuItemProps> = ({ name, date, timings }) => {
  return (
    <EventMenuItemBase name={name} date={date} timings={timings}>
      <Link style={s.menuItem.title.common}>
        {'We\'re tying things up'}
      </Link>
    </EventMenuItemBase>
  )
}

const EventAwaitingResponsesMenuItem: FC<StatusEventMenuItemProps> = ({ name, date, timings }) => {
  return (
    <EventMenuItemBase name={name} date={date} timings={timings}>
      <Link style={s.menuItem.title.common}>
        {'We\'re working on it'}
      </Link>
    </EventMenuItemBase>
  )
}
